// Time Raid — game data
// Mirrors Budget Commander's shape on purpose: Recon (tag real data) -> Formation
// (build a zero-slack schedule) -> Raid (live it) -> Debrief (score + save).

export const BLOCK_COUNT = 6;
export const BLOCK_LENGTH_LABEL = "90 min";
export const STARTING_FOCUS = 100;

// ---------------------------------------------------------------------------
// STAGE 1 — RECON: today's incoming requests, pulled straight from your inbox
// and calendar. Quadrant is fixed (that classification work is "done" for
// you); the player's job is to tag Urgent and Important, which is exactly
// the two-axis judgment the Eisenhower Matrix requires.
// ---------------------------------------------------------------------------
export const TASKS = [
  {
    id: "server-down",
    time: "8:45 AM",
    description: "Client escalation — the checkout server is down and sales are bleeding right now.",
    minutes: 90,
    quadrant: "Q1",
    correctUrgent: true,
    correctImportant: true,
  },
  {
    id: "presentation",
    time: "9:10 AM",
    description: "Tomorrow morning's board presentation still needs slides.",
    minutes: 90,
    quadrant: "Q1",
    correctUrgent: true,
    correctImportant: true,
  },
  {
    id: "exam-study",
    time: "—",
    description: "Study block for the certification exam next month.",
    minutes: 90,
    quadrant: "Q2",
    correctUrgent: false,
    correctImportant: true,
  },
  {
    id: "workout",
    time: "—",
    description: "Workout — the one habit that keeps your energy up all week.",
    minutes: 90,
    quadrant: "Q2",
    correctUrgent: false,
    correctImportant: true,
  },
  {
    id: "car-check",
    time: "—",
    description: "That weird noise the car's been making — get it looked at before it strands you.",
    minutes: 90,
    quadrant: "Q2",
    correctUrgent: false,
    correctImportant: true,
  },
  {
    id: "slack-ping",
    time: "10:02 AM",
    description: '"Got a sec?" — a coworker Slack message that turns into 20 minutes.',
    minutes: 15,
    quadrant: "Q3",
    correctUrgent: true,
    correctImportant: false,
    resolutionOptions: [
      { id: "batch", label: "Reply later, batched with other quick messages", tag: "good", note: "Batching keeps you out of a start-stop spiral." },
      { id: "delegate", label: "Point them to a teammate or the FAQ doc", tag: "good", note: "Not everything needs to run through you." },
      { id: "frog", label: "Answer right now, mid-Frog-Block", tag: "bad", note: "Your most important task just got interrupted for something that could've waited." },
    ],
  },
  {
    id: "status-meeting",
    time: "11:00 AM",
    description: "A meeting invite for a status update that could've been a two-line email.",
    minutes: 60,
    quadrant: "Q3",
    correctUrgent: true,
    correctImportant: false,
    resolutionOptions: [
      { id: "decline", label: "Decline, and offer a written update instead", tag: "good", note: "\u201cI want to keep today's focus time protected \u2014 could I send a written update instead?\u201d That's an assertive, respectful no." },
      { id: "delegate", label: "Send a teammate in your place", tag: "good", note: "Delegation just freed up a full block." },
      { id: "attend", label: "Attend anyway — easier than pushing back", tag: "bad", note: "That's 60 minutes of your day gone to something that didn't need you there." },
    ],
  },
  {
    id: "new-episode",
    time: "—",
    description: "The next episode of your new favorite show just dropped.",
    minutes: 45,
    quadrant: "Q4",
    correctUrgent: false,
    correctImportant: false,
    resolutionOptions: [
      { id: "cut", label: "Cut it — save it for tonight, after the real work's done", tag: "good", note: "It'll still be there. Your focus window won't wait the same way." },
      { id: "oneepisode", label: '"Just one episode" real quick', tag: "bad", note: "You know how that goes." },
    ],
  },
];

export const SCHEDULABLE_IDS = ["server-down", "presentation", "exam-study", "workout", "car-check"];
export const Q3_IDS = ["slack-ping", "status-meeting"];
export const Q4_IDS = ["new-episode"];

export const QUADRANT_META = {
  Q1: { label: "Do First", sub: "Urgent + Important", accent: "danger" },
  Q2: { label: "Schedule", sub: "Important, Not Urgent", accent: "lime" },
  Q3: { label: "Delegate or Decline", sub: "Urgent, Not Important", accent: "gold" },
  Q4: { label: "Cut", sub: "Neither", accent: "mist" },
};

export const ENV_TOGGLES = [
  { id: "notifications", label: "Notifications Off", description: "No dinging, no buzzing — the tyranny of the urgent starts with your phone." },
  { id: "desk", label: "Desk Cleared", description: "One task in front of you at a time. Clutter reads as unfinished work to your brain." },
  { id: "timer", label: "Visible Timer Set", description: "A clock in view fights time blindness — you'll feel the block passing, not just guess." },
];

// ---------------------------------------------------------------------------
// STAGE 3 — RAID: one event per schedulable task, keyed by task id so it
// plays out wherever the player actually placed it (including Block 1, the
// Frog Block). `distraction: true` events can be deflected with a Focus
// Shield earned from Formation-stage environment setup.
// ---------------------------------------------------------------------------
export const RAID_EVENTS = {
  "server-down": {
    title: "System Fire",
    text: "Mid-fix, a coworker leans into your doorway: \u201cGot 20 minutes? I just need to vent about my week.\u201d",
    distraction: true,
    choices: [
      { id: "redirect", label: "\u201cNot now \u2014 let's grab lunch and catch up instead.\u201d", tag: "good", focusDelta: 0, result: "A clear, kind boundary. The server fix stays on track." },
      { id: "vent", label: "Let them vent for 20 minutes", tag: "bad", focusDelta: -15, result: "The outage took 20 minutes longer to resolve than it should have." },
    ],
  },
  presentation: {
    title: "Scope Creep",
    text: "Your manager messages: \u201cCan you also add three more slides on Q3 metrics?\u201d",
    distraction: true,
    choices: [
      { id: "boundary", label: "\u201cI can get you those tomorrow \u2014 today's set is scoped for the board ask.\u201d", tag: "good", focusDelta: 0, result: "The ask got a real answer, not a scramble. The deck stays on schedule." },
      { id: "cram", label: "Say yes and cram it in", tag: "bad", focusDelta: -15, result: "The deck grew, the quality dropped, and the block ran long." },
    ],
  },
  "exam-study": {
    title: "Perfectionism Trap",
    text: "You want your notes flawless before moving on \u2014 and you're stuck rereading the same page for the fourth time.",
    distraction: false,
    choices: [
      { id: "kaizen", label: "Apply Kaizen \u2014 just do the next tiny step, one page at a time", tag: "good", focusDelta: 5, result: "Small, unglamorous progress. It adds up." },
      { id: "reread", label: "Keep rereading until it feels perfect", tag: "bad", focusDelta: -15, result: "\u201cPerfect\u201d never arrived, and the block ran out with half the material uncovered." },
    ],
  },
  workout: {
    title: "Low Motivation",
    text: "You really don't feel like it today.",
    distraction: false,
    choices: [
      { id: "five-second", label: "Use the 5-Second Rule \u2014 count down and just start moving", tag: "good", focusDelta: 5, result: "Momentum showed up after you did, not before." },
      { id: "skip", label: "Skip it \u2014 you'll definitely do it tomorrow", tag: "bad", focusDelta: -10, result: "\u201cTomorrow\u201d is doing a lot of work in that sentence." },
    ],
  },
  "car-check": {
    title: "Batching Opportunity",
    text: "While you're out for the car, you realize you could also grab groceries and drop off a package on the same trip.",
    distraction: false,
    choices: [
      { id: "batch", label: "Batch all three errands into one trip", tag: "good", focusDelta: 5, result: "One trip instead of three \u2014 task batching in action." },
      { id: "single", label: "Just handle the car and call it done", tag: "neutral", focusDelta: 0, result: "Handled, but you'll be making two more trips later this week." },
    ],
  },
};

export const BUFFER_EVENT = {
  title: "The Buffer Pays Off",
  text: "A genuine emergency call comes in \u2014 the exact kind of thing you can't schedule around. Because this block was left open on purpose, it absorbs the hit without knocking anything else off the day.",
};

export const END_OF_DAY_EVENT = {
  title: "Two-Minute Rule Check",
  text: "It's 4:45. Two quick emails are still sitting there \u2014 each one would take under two minutes to clear.",
  choices: [
    { id: "now", label: "Knock them out now, both under 2 minutes", tag: "good", focusDelta: 5, result: "Cleared before they could turn into tomorrow's clutter \u2014 the 2-Minute Rule at work." },
    { id: "tomorrow", label: "Add them to tomorrow's list", tag: "bad", focusDelta: -5, result: "Small, but tomorrow's Frog Block just got a little more crowded." },
  ],
};
